import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Hammer,
  ExternalLink
} from 'lucide-react';
import { companyInfo } from '../data/mock';

const CarpentryJoinery = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', postcode: '', message: '', honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: `[Carpentry Enquiry] ${formData.message}`,
          trade_type: 'carpentry',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/carpentry-joinery'
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    { title: 'Internal Doors', desc: 'New door installation, door repairs, adjustments, replacements and fire doors' },
    { title: 'Skirting Boards', desc: 'New skirting, repairs, replacements and matching to existing profiles' },
    { title: 'Architrave', desc: 'Door frame surrounds, repairs and replacements' },
    { title: 'Boxing-In', desc: 'Concealing pipework, meters and unsightly features' },
    { title: 'Shelving', desc: 'Fitted shelving, alcove shelving, floating shelves and storage solutions' },
    { title: 'Alcove Units', desc: 'Built-in cupboards, wardrobes and storage to maximise space' },
    { title: 'Cupboards & Storage', desc: 'Bespoke storage solutions for awkward spaces' },
    { title: 'Timber Repairs', desc: 'Structural timber, damaged woodwork, rot repairs' },
    { title: 'Staircase Work', desc: 'Handrails, spindles, treads and general staircase repairs' },
    { title: 'Window Repairs', desc: 'Timber window repairs, sash window maintenance' }
  ];

  const benefits = [
    'Skilled, experienced carpenters',
    'Quality materials and finishes',
    'Clean, tidy working',
    'Attention to detail',
    '£5 million public liability insurance',
    '12-month workmanship guarantee'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Carpentry & Joinery Services Manchester | Doors, Skirting, Built-In Storage | Expert Property Maintenance</title>
        <meta name="description" content="Carpentry and joinery services in Manchester for landlords and homeowners. Doors, skirting, architrave, boxing-in, shelving, alcove units. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/carpentry-joinery" />
      </Helmet>

      <Header variant="default" companyPhone={companyInfo.phone} />

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Hammer className="w-4 h-4" />
              Carpentry & Joinery
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Carpentry & Joinery Services
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Professional carpentry and joinery services for landlords, letting agents and homeowners 
              across Greater Manchester. From door repairs to bespoke built-in storage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">
                  Request a Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                Quality Carpentry & Joinery
              </h2>
              <p className="text-lg text-brand-slate mb-6">
                Good carpentry makes a real difference to a property. Doors that hang correctly, skirting 
                that's properly fitted, built-in storage that maximises space — these details matter for 
                both landlords and tenants.
              </p>
              <p className="text-brand-slate mb-6">
                Expert Property Maintenance provides skilled carpentry services as part of our property 
                maintenance offering. Whether it's repairing damaged doors in a rental property, installing 
                new skirting after a refurbishment, or building alcove storage in a living room.
              </p>
              <p className="text-brand-slate mb-8">
                Our carpenters work to high standards with attention to detail — matching existing profiles, 
                achieving clean finishes, and leaving the work area tidy.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Expert Carpenter Brand Link */}
              <div className="mt-8 p-4 bg-brand-grey border border-slate-200 rounded-xl">
                <p className="text-sm text-brand-slate mb-2">Our specialist carpentry brand:</p>
                <a 
                  href="https://expert-carpenter.co.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-brand-yellow hover:text-yellow-300 font-semibold"
                >
                  Expert Carpenter <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
            
            <div className="bg-brand-grey rounded-2xl p-8">
              <h3 className="text-xl font-bold text-brand-navy mb-6">Carpentry Services</h3>
              <div className="space-y-4">
                {services.map((service) => (
                  <div key={service.title} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-brand-navy">{service.title}</span>
                      <p className="text-sm text-brand-slate">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Landlords */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-4 text-center">
            Carpentry for Rental Properties
          </h2>
          <p className="text-lg text-brand-slate text-center mb-12 max-w-2xl mx-auto">
            Common carpentry requirements for landlords and letting agents.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Door Repairs', desc: 'Damaged doors, sticking doors, hinge problems — common issues in rental properties' },
              { title: 'Skirting Replacement', desc: 'Replacing damaged or outdated skirting boards during refurbishments' },
              { title: 'Fire Doors', desc: 'Installation and maintenance of fire doors in HMOs and flats' },
              { title: 'Built-In Wardrobes', desc: 'Maximising bedroom storage in rental properties' },
              { title: 'Kitchen Repairs', desc: 'Cabinet repairs, drawer fixes, door adjustments' },
              { title: 'General Woodwork', desc: 'Timber repairs, architrave, boxing-in' }
            ].map((item) => (
              <Card key={item.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{item.title}</h3>
                  <p className="text-brand-slate text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/handyman-services" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Handyman Services</h4>
              <p className="text-sm text-brand-slate">General repairs & odd jobs</p>
            </a>
            <a href="/painting-decorating" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Painting & Decorating</h4>
              <p className="text-sm text-brand-slate">Finish after carpentry</p>
            </a>
            <a href="/void-property-works" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Void Property Works</h4>
              <p className="text-sm text-brand-slate">Complete turnarounds</p>
            </a>
            <a href="/property-refurbishment" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Property Refurbishment</h4>
              <p className="text-sm text-brand-slate">Full renovations</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Get a Carpentry Quote</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about your carpentry requirements and we will provide a quote.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch with a quote.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Request a Quote</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Postcode</label>
                      <Input value={formData.postcode} onChange={(e) => setFormData({...formData, postcode: e.target.value})} placeholder="e.g., M1 1AA" className="h-12" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Describe the Work *</label>
                      <Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="What carpentry work do you need?" rows={4} />
                    </div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default CarpentryJoinery;
